//	Block data → render spec. The grid (table.columns / table.rows) is the editorial source of
//	truth; series are derived here so transposing or re-roling columns never rewrites data.
import { SLOTS } from "./palette.js";
import { formatValue, unitLabel } from "./format.js";

export const TYPES = ["line", "area", "column", "bar", "pie", "donut"];

export const LIMITS = Object.freeze({
	rows: 500,
	columns: 12,
	series: SLOTS,
	pieSlices: 8,
	pieComfortable: 4,
	barCategories: 60,
	heavyRows: 200,
	cell: 160,
	label: 80,
	title: 160,
	subtitle: 240,
	caption: 400,
	source: 160,
	url: 500,
	alt: 600,
	unit: 24,
	annotations: 12,
	annotationLabel: 80,
	absMax: 1e15,
});

export const isNum = (v) => typeof v === "number" && Number.isFinite(v);

export function cellText(v) {
	if (v == null) return "";
	if (typeof v === "number") return String(v);
	return String(v);
}

//	the category column: the one with role x, else the first
export function xColumn(columns) {
	const i = (columns || []).findIndex((c) => c && c.role === "x");
	return i < 0 ? 0 : i;
}

//	palette slot per charted series column (explicit slots win, the rest take the lowest free one in
//	order — colour follows the column, never the rank); the grid header and the chart share this
export function assignSlots(columns) {
	const cols = Array.isArray(columns) ? columns : [];
	const xCol = xColumn(cols);
	const charted = [];
	cols.forEach((c, i) => {
		if (i !== xCol && c && c.role === "series" && charted.length < SLOTS) charted.push(i);
	});
	const slots = {};
	const taken = new Set();
	for (const i of charted) {
		const slot = cols[i].color;
		if (Number.isInteger(slot) && slot >= 1 && slot <= SLOTS) {
			slots[i] = slot;
			taken.add(slot);
		}
	}
	let next = 1;
	for (const i of charted) {
		if (slots[i]) continue;
		while (taken.has(next) && next < SLOTS) next++;
		slots[i] = next;
		taken.add(next);
	}
	return slots;
}

//	{ xCol, x: string[], series: [{ col, name, color, values }], rows }
export function normalize(data, { allSeries = false } = {}) {
	const columns = Array.isArray(data?.table?.columns) ? data.table.columns : [];
	const rows = Array.isArray(data?.table?.rows) ? data.table.rows : [];
	const xCol = xColumn(columns);
	const x = rows.map((r) => cellText(r?.[xCol]));
	const slots = assignSlots(columns);
	const indices = columns.map((_, i) => i).filter((i) => i !== xCol && columns[i]?.role === "series");
	const series = (allSeries ? indices : indices.slice(0, LIMITS.series)).map((i, k) => ({
		col: i,
		name: (columns[i].label || "").trim() || `Seria ${k + 1}`,
		color: slots[i],
		values: rows.map((r) => (isNum(r?.[i]) ? r[i] : null)),
	}));
	return { xCol, x, series, rows: rows.length, xLabel: (columns[xCol]?.label || "").trim() };
}

//	point annotations address a column and a row by index: keep them pointing at the same cell
//	across grid edits, drop them when the cell is gone or the table was replaced
export function remapAnnotations(annotations, op) {
	const list = Array.isArray(annotations) ? annotations.filter(Boolean) : [];
	const points = (fn) => list.map((a) => (a.kind === "point" ? fn(a) : a)).filter(Boolean);
	switch (op && op.kind) {
		case "swapColumns":
			return points((a) => (a.column === op.i ? { ...a, column: op.j } : a.column === op.j ? { ...a, column: op.i } : a));
		case "deleteColumn":
			return points((a) => (a.column === op.c ? null : a.column > op.c ? { ...a, column: a.column - 1 } : a));
		case "deleteRow":
			return points((a) => (a.row === op.r ? null : a.row > op.r ? { ...a, row: a.row - 1 } : a));
		case "replace":
			return list.filter((a) => a.kind !== "point");
		default:
			return list;
	}
}

//	source link: http(s) only; a bare domain gets https://, anything else is dropped
export function normalizeUrl(s) {
	if (typeof s !== "string") return "";
	let v = s.trim();
	if (!v) return "";
	if (/^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(v)) v = "https://" + v;
	try {
		const u = new URL(v);
		return u.protocol === "http:" || u.protocol === "https:" ? u.href : "";
	} catch (_e) {
		return "";
	}
}

export function hasChartableData(data) {
	return normalize(data).series.some((s) => s.values.some(isNum));
}

const PIE = (t) => t === "pie" || t === "donut";

//	admin-facing warnings (English, per project rule); never blocks saving
export function validateDetailed(data) {
	const out = [];
	const spec = normalize(data);
	const type = data?.type;
	const rows = data?.table?.rows || [];
	const columns = data?.table?.columns || [];
	const all = normalize(data, { allSeries: true });
	if (!PIE(type) && all.series.length > LIMITS.series) out.push({ level: "warn", text: `Only the first ${LIMITS.series} series are plotted. ${all.series.length - LIMITS.series} additional series remain in the data table; mark unused columns Ignore or split the chart.` });
	if (isNum(data?.options?.yMin) && isNum(data?.options?.yMax) && data.options.yMin >= data.options.yMax) out.push({ level: "error", text: "Axis minimum must be below maximum. Automatic bounds are used until corrected." });
	if (!spec.series.length) out.push({ level: "error", text: "Mark at least one column as a series." });
	if (!spec.series.some((s) => s.values.some(isNum))) out.push({ level: "error", text: "The series columns contain no numbers yet." });
	let bad = 0;
	for (const s of spec.series) for (const r of rows) if (r[s.col] != null && r[s.col] !== "" && !isNum(r[s.col])) bad++;
	if (bad) out.push({ level: "warn", text: `${bad} cell${bad === 1 ? "" : "s"} in series columns ${bad === 1 ? "is" : "are"} not numeric and will be shown as gaps.` });
	if (PIE(type)) {
		if (spec.series.length > 1) out.push({ level: "warn", text: "Pie and donut charts use the first series only." });
		if (rows.length > LIMITS.pieSlices) out.push({ level: "warn", text: `Only the ${LIMITS.pieSlices - 1} largest slices are shown; the rest are folded into “Altele”.` });
		else if (rows.length > LIMITS.pieComfortable) out.push({ level: "warn", text: "More than 4 slices are hard to tell apart; consider a bar chart." });
		if (spec.series[0] && spec.series[0].values.some((v) => isNum(v) && v < 0)) out.push({ level: "warn", text: "Negative values cannot be drawn in a pie chart and are skipped." });
	}
	if (data?.options?.stacked && spec.series.length < 2) out.push({ level: "warn", text: "Stacking needs at least two series." });
	if (data?.options?.stacked && spec.series.some((s) => s.values.some((v) => isNum(v) && v < 0)) && spec.series.some((s) => s.values.some((v) => isNum(v) && v > 0)))
		out.push({ level: "warn", text: "Mixed positive and negative values are stacked on separate sides of the baseline." });
	if ((type === "bar" || type === "column") && rows.length > LIMITS.barCategories) out.push({ level: "warn", text: `More than ${LIMITS.barCategories} categories make bars unreadable; consider a line chart.` });
	if ((type === "bar" || type === "column") && isNum(data?.options?.yMin) && data.options.yMin > 0) out.push({ level: "warn", text: "Bars should start at zero; a positive axis minimum exaggerates differences." });
	if (rows.length > LIMITS.heavyRows) out.push({ level: "warn", text: `${rows.length} rows make the page heavier and the chart dense; consider aggregating.` });
	if (columns.length && !columns.some((c) => c.role === "x")) out.push({ level: "warn", text: "No column is marked as the category (X) column; the first column is used." });
	if (data?.sourceUrl && String(data.sourceUrl).trim() && !normalizeUrl(data.sourceUrl)) out.push({ level: "warn", text: "The source URL is not a valid web address (http:// or https://) and will not be linked." });
	for (const a of data?.annotations || []) {
		if (a && a.kind === "point" && !(spec.series.some((s) => s.col === a.column) && a.row >= 0 && a.row < rows.length)) out.push({ level: "warn", text: `The point note “${a.label || ""}” points at a cell that no longer exists.` });
	}
	return out;
}

const TYPE_RO = { line: "cu linii", area: "cu arii", column: "cu coloane", bar: "cu bare", pie: "circular", donut: "tip inel" };

//	Romanian alt text when the editor left it empty
export function summaryText(data) {
	const spec = normalize(data);
	const type = data?.type || "line";
	const options = data?.options || {};
	const parts = [];
	parts.push(`Grafic ${TYPE_RO[type] || ""}`.trim());
	if (data?.title) parts[0] += `: ${data.title}`;
	if (PIE(type)) {
		const s = spec.series[0];
		if (s) {
			const items = spec.x.map((l, i) => ({ l, v: s.values[i] })).filter((it) => isNum(it.v) && it.v > 0);
			const total = items.reduce((t, it) => t + it.v, 0);
			const top = items.slice().sort((a, b) => b.v - a.v)[0];
			parts.push(`${items.length} categorii`);
			if (top && total) parts.push(`cea mai mare: ${top.l} (${Math.round((top.v / total) * 100)}%)`);
		}
	} else {
		if (spec.series.length === 1) parts.push(`seria „${spec.series[0].name}”`);
		else if (spec.series.length) parts.push(`${spec.series.length} serii (${spec.series.map((s) => s.name).join(", ")})`);
		if (spec.x.length) parts.push(spec.x.length > 1 ? `${spec.x.length} categorii, de la ${spec.x[0]} la ${spec.x[spec.x.length - 1]}` : `categoria ${spec.x[0]}`);
		const all = spec.series.flatMap((s) => s.values).filter(isNum);
		if (all.length) parts.push(`valori între ${formatValue(Math.min(...all), options)} și ${formatValue(Math.max(...all), options)}`);
	}
	if (data?.source) parts.push(`Sursa: ${data.source}`);
	return parts.join(". ") + ".";
}

//	rows for the accessible table twin: header + formatted cells (x column first)
export function tableRows(data) {
	const spec = normalize(data, { allSeries: true });
	const options = data?.options || {};
	const unit = unitLabel(options);
	const header = [spec.xLabel || "", ...spec.series.map((s) => (unit && !unit.startsWith("%") ? `${s.name} (${unit})` : s.name))];
	const rows = spec.x.map((label, i) => [label, ...spec.series.map((s) => (isNum(s.values[i]) ? formatValue(s.values[i], { ...options, unit: undefined, currency: undefined, yFormat: options.yFormat === "percent" ? "percent" : "number" }) : "—"))]);
	return { header, rows };
}
